import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Calendar, User, ExternalLink, ChevronRight } from "lucide-react";
import notesImg from "@/assets/notes-watercolor.png";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { INFO_TYP, infoAdresse, kommendeLesen } from "@/lib/termine/infoveranstaltungen";

interface AktuellesItem {
  id: string;
  titel: string;
  beschreibung: string | null;
  bild_url: string | null;
  link: string | null;
  ist_externer_link: boolean;
  kategorie: string;
  kategorie_farbe: string;
  datum_anzeige: string | null;
  dozent: string | null;
  tags: string[] | null;
}

const KATEGORIE_FARBEN: Record<string, string> = {
  "Themen-Webinar": "#2dd4bf",
  "Kreativ-Webinar": "#f97316",
  "Kostenloses Webinar": "#f97316",
  "Online-Kurs": "#8b5cf6",
  "Shop-Produkt": "#ec4899",
  "Info-Webinar": "#3b82f6",
  [INFO_TYP]: "#3b82f6",
};

const AktuellesSection = () => {
  const [items, setItems] = useState<AktuellesItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const now = new Date().toISOString();

      // Two parallel queries
      const [termineRes, aktuellesRes, infosRes] = await Promise.all([
        // 1. Dynamic upcoming termine (Themenwebinare + Kostenlose Webinare)
        supabase
          .from("termine")
          .select("id, titel, beschreibung, bild_url, slug, typ, datum, dozent")
          .in("typ", ["Themenwebinare", "Kostenlose Webinare"])
          .gte("datum", now)
          .eq("status", "live")
          .order("datum", { ascending: true })
          .limit(5),
        // 2. Fixed items from aktuelles (permanent entries)
        supabase
          .from("aktuelles")
          .select("*")
          .in("kategorie", ["Online-Kurs", "Shop-Produkt"])
          .order("sortierung", { ascending: true }),
        // 3. Kommende Infoveranstaltungen (Sitzung 14b: „ganz normal wie einen Termin“), eigene Tabellen
        supabase.rpc("marketing_info_kommende", { p_grenze: 3 }),
      ]);

      // Convert termine to AktuellesItem format
      const terminItems: (AktuellesItem & { beginn: string })[] = (termineRes.data || []).map((t: any) => {
        const kategorie = t.typ === "Themenwebinare" ? "Themen-Webinar" : "Kostenloses Webinar";
        const datumFormatted = t.datum
          ? format(new Date(t.datum), "dd.MM.yyyy | HH:mm 'Uhr'", { locale: de })
          : null;

        return {
          id: t.id,
          titel: t.titel,
          beschreibung: t.beschreibung,
          bild_url: t.bild_url,
          link: `/termin/${t.slug}`,
          ist_externer_link: false,
          kategorie,
          kategorie_farbe: KATEGORIE_FARBEN[kategorie] || "#6b7280",
          datum_anzeige: datumFormatted,
          dozent: t.dozent,
          tags: null,
          beginn: t.datum,
        };
      });

      // Infoveranstaltungen nach Datum zwischen die Termine (höchstens fünf wie bisher)
      const infoItems = kommendeLesen(infosRes.error ? [] : infosRes.data).map((i) => ({
        id: `info-${i.termin_id}`,
        titel: i.titel,
        beschreibung: i.untertitel,
        bild_url: i.bild_url,
        link: infoAdresse(i, "startseite"),
        ist_externer_link: false,
        kategorie: INFO_TYP,
        kategorie_farbe: KATEGORIE_FARBEN[INFO_TYP],
        datum_anzeige: format(new Date(i.beginn), "dd.MM.yyyy | HH:mm 'Uhr'", { locale: de }),
        dozent: null,
        tags: null,
        beginn: i.beginn,
      }));
      const dynamicItems: AktuellesItem[] = [...terminItems, ...infoItems]
        .sort((a, b) => new Date(a.beginn).getTime() - new Date(b.beginn).getTime())
        .slice(0, 5);

      // Map fixed aktuelles items
      const fixedItems: AktuellesItem[] = (aktuellesRes.data || []).map((a: any) => ({
        id: a.id,
        titel: a.titel,
        beschreibung: a.beschreibung,
        bild_url: a.bild_url,
        link: a.link,
        ist_externer_link: a.ist_externer_link ?? false,
        kategorie: a.kategorie,
        kategorie_farbe: a.kategorie_farbe || KATEGORIE_FARBEN[a.kategorie] || "#6b7280",
        datum_anzeige: a.datum_anzeige,
        dozent: a.dozent,
        tags: a.tags,
      }));

      // Interleave: Termin, Fixed, Termin, Fixed, ... then remaining termine
      const merged: AktuellesItem[] = [];
      let di = 0;
      let fi = 0;
      while (merged.length < 8 && (di < dynamicItems.length || fi < fixedItems.length)) {
        if (di < dynamicItems.length) {
          merged.push(dynamicItems[di++]);
        }
        if (merged.length < 8 && fi < fixedItems.length) {
          merged.push(fixedItems[fi++]);
        }
      }

      setItems(merged);
    } catch (error) {
      console.error("Fehler beim Laden:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <section className="py-24 relative overflow-hidden bg-gradient-to-br from-background via-muted/30 to-background">
      {/* Decorative watercolor accents */}
      <div className="absolute top-10 right-0 w-80 h-80 opacity-15 animate-float">
        <img src={notesImg} alt="" className="w-full h-full object-contain" />
      </div>
      <div className="absolute bottom-10 left-0 w-64 h-64 opacity-10 animate-float" style={{ animationDelay: '1s' }}>
        <img src={notesImg} alt="" className="w-full h-full object-contain transform scale-x-[-1]" />
      </div>
      
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16 space-y-6 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold">
            <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent animate-gradient">
              Aktuelles
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Bleib auf dem Laufenden mit unseren neuesten Angeboten und Entwicklungen
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((item, index) => {
            const CardWrapper = item.link ? 'a' : 'div';
            const cardProps = item.link ? {
              href: item.link,
              target: item.ist_externer_link ? "_blank" : undefined,
              rel: item.ist_externer_link ? "noopener noreferrer" : undefined,
            } : {};

            return (
              <CardWrapper key={item.id} {...cardProps} className="block group/card">
                 <Card
                  className="h-full transition-all duration-500 border border-border/50 bg-card/95 backdrop-blur-sm hover:shadow-2xl hover:shadow-primary/10 hover:scale-[1.02] hover:-translate-y-1 hover:border-primary/40 overflow-hidden animate-fade-in relative"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  <div className="relative h-40 sm:h-48 overflow-hidden bg-gradient-to-br from-muted/20 to-muted/40">
                    {item.bild_url && (
                      <>
                        <img 
                          src={item.bild_url} 
                          alt={item.titel}
                          className="w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                      </>
                    )}
                    <Badge 
                      className="absolute top-3 right-3 text-white border-0 shadow-lg px-3 py-1.5 font-semibold"
                      style={{ backgroundColor: item.kategorie_farbe }}
                    >
                      {item.kategorie}
                    </Badge>
                    {item.link && (
                      <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover/card:opacity-100 transition-all duration-300 transform -translate-x-2 group-hover/card:translate-x-0">
                        {item.ist_externer_link ? (
                          <ExternalLink className="w-4 h-4 text-primary" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-primary" />
                        )}
                      </div>
                    )}
                  </div>
                  
                  <CardHeader className="space-y-3 pb-3 relative z-10">
                    <CardTitle className="text-lg leading-tight text-foreground group-hover/card:text-primary transition-colors duration-300 flex items-start justify-between gap-2">
                      <span className="font-bold">{item.titel}</span>
                      {item.link && (
                        <ChevronRight className="w-5 h-5 text-primary shrink-0 transform group-hover/card:translate-x-1 transition-transform duration-300" />
                      )}
                    </CardTitle>
                    {item.beschreibung && (
                      <CardDescription className="text-sm leading-relaxed line-clamp-2">
                        {item.beschreibung}
                      </CardDescription>
                    )}
                  </CardHeader>

                  {(item.datum_anzeige || item.dozent || (item.tags && item.tags.length > 0)) && (
                    <div className="mx-6 border-t border-border/50" />
                  )}

                  <CardContent className="space-y-3 pt-4 relative z-10">
                    {item.datum_anzeige && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4 shrink-0" />
                        <span>{item.datum_anzeige}</span>
                      </div>
                    )}

                    {item.dozent && (
                      <div className="flex items-start gap-2 text-sm">
                        <User className="w-4 h-4 shrink-0 text-primary mt-0.5" />
                        <span className="text-foreground font-medium">{item.dozent}</span>
                      </div>
                    )}

                    {item.tags && item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {item.tags.map((tag) => (
                          <Badge 
                            key={tag} 
                            variant="outline" 
                            className="text-xs px-2.5 py-1 border-primary/40 text-primary hover:bg-primary/10 hover:border-primary transition-all duration-300 hover:scale-105 cursor-default"
                          >
                            #{tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </CardWrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AktuellesSection;
